import React, { useEffect, useState, useMemo, useCallback, Suspense, lazy } from 'react';
import { fetchSpaces } from '../api/spaceApi';
import { useNavigate } from 'react-router-dom';
import { getUserNotification } from '../api/notificationApi';
import { getFavorites } from '../api/userApi';
import { Helmet } from 'react-helmet-async';

// Lazy loading for non-critical components
const SidebarMenu = lazy(() => import('../componets/SidebarMenu'));
const Footer = lazy(() => import('../componets/Footer'));
const Logo = lazy(() => import('../componets/Logo'));
const MainContent = lazy(() => import('../componets/MainContent'));
const Popup = lazy(() => import('../componets/Popup'));
const Pagination = lazy(() => import('../componets/Pagination'));
const BuildingTypeButtons = lazy(() => import('../componets/BuildingTypeButtons'));
const Banner = lazy(() => import('../componets/Banner'));
const SlideOver = lazy(() => import('../componets/GuestSlide'));

interface Space {
  id: string;
  ownerName: string;
  region: string;
  location: string;
  price: number;
  status: string;
  mainImageUrl: string;
  createdAt: string;
  updatedAt: string;
  tags: string[];
  reservationCount: number;
  ownerCertified: boolean;
  buildingType: string;
  isFavorite: boolean;
}

const Spaces: React.FC = () => {
  const [recentSpaces, setRecentSpaces] = useState<Space[]>([]);
  const [buildingSpaces, setBuildingSpaces] = useState<Space[]>([]);

  const [region, setRegion] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<string | undefined>(undefined);
  const [buildingType, setBuildingType] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [recentPage, setRecentPage] = useState<number>(1);
  const [recentTotalPages, setRecentTotalPages] = useState<number>(1);

  const [buildingPage, setBuildingPage] = useState<number>(1);
  const [buildingTotalPages, setBuildingTotalPages] = useState<number>(1);

  const [isMobile, setIsMobile] = useState<boolean>(() => window.innerWidth < 1024);
  const [unreadSpaceNoti, setUnreadSpaceNoti] = useState<number>(0);
  const [unreadReservationNoti, setUnreadReservationNoti] = useState<number>(0);
  const [isSlideOverOpen, setIsSlideOverOpen] = useState<boolean>(false);
  const [favoriteSpaceIds, setFavoriteSpaceIds] = useState<Set<string>>(new Set());

  const navigate = useNavigate();

  const handleUploadSpace = useCallback(() => {
    const token = localStorage.getItem('user_token');
    if (!token) {
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }
    navigate('/upload-space');
  }, [navigate]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('user_token');
    setIsLoggedIn(false);
    navigate('/login');
  }, [navigate]);

  // 관심공간 목록 불러오기
  const fetchFavorites = useCallback(async () => {
    const token = localStorage.getItem('user_token');
    if (!token) {
      return;
    }

    try {
      const response = await getFavorites();
      
      const favoriteIds = response.body || response;
      const favoriteIdsSet = new Set<string>(favoriteIds.map((id: number) => id.toString()));
      
      setFavoriteSpaceIds(favoriteIdsSet);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('user_token');
    const loggedIn = !!token;
    setIsLoggedIn(loggedIn);

    if (loggedIn) {
      getUserNotification()
        .then(data => {
          setUnreadSpaceNoti(data.spaces);
          setUnreadReservationNoti(data.reservations);
        })
        .catch(error => {
          console.error('Failed to fetch notifications:', error);
        });

      fetchFavorites();
    }
  }, [fetchFavorites]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 최근등록 공간 데이터 fetch (buildingType 제외)
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await fetchSpaces({
          region,
          sort,
          page: recentPage,
          limit: isMobile ? 4 : 10,
        });
        
        if (isMounted) {
          // 관심공간 정보와 함께 공간 데이터 설정
          const spacesWithFavorites = data.spaces.map((space: any) => {
            const isFavorite = favoriteSpaceIds.has(space.id.toString());
            return {
              ...space,
              isFavorite
            };
          });
          
          setRecentSpaces(spacesWithFavorites);
          setRecentTotalPages(data.totalPages);
          setRecentPage(data.currentPage);
        }
      } catch (error) {
        console.error('Failed to fetch spaces:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // 클린업 함수
    return () => {
      isMounted = false;
    };
  }, [region, sort, recentPage, isMobile, favoriteSpaceIds]);

  // 건물유형별 공간 데이터 fetch
  useEffect(() => {
    if (!buildingType) return;
    
    let isMounted = true;
    
    const fetchBuildingSpaces = async () => {
      try {
        const result = await fetchSpaces({
          buildingType,
          region,
          sort,
          page: buildingPage,
          limit: isMobile ? 4 : 10,
        });

        if (isMounted) {
          // 관심공간 정보와 함께 공간 데이터 설정
          const spacesWithFavorites = result.spaces.map((space: any) => {
            const isFavorite = favoriteSpaceIds.has(space.id.toString());
            return {
              ...space,
              isFavorite
            };
          });
          
          setBuildingSpaces(spacesWithFavorites);
          setBuildingTotalPages(result.totalPages);
        }
      } catch (error) {
        console.error('Failed to fetch building spaces:', error);
      }
    };

    fetchBuildingSpaces();

    return () => {
      isMounted = false;
    };
  }, [buildingType, region, sort, buildingPage, isMobile, favoriteSpaceIds]);

  const [touchStartX, setTouchStartX] = useState<number>(0);
  
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStartX(e.changedTouches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const distance = touchStartX - touchEndX;
    if (distance > 50 && recentPage < recentTotalPages) setRecentPage(prev => prev + 1);
    else if (distance < -50 && recentPage > 1) setRecentPage(prev => prev - 1);
  }, [touchStartX, recentPage, recentTotalPages]);

  // 건물유형별 공간 터치 이벤트 핸들러
  const handleBuildingTouchEnd = useCallback((e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const distance = touchStartX - touchEndX;
    if (distance > 50 && buildingPage < buildingTotalPages) setBuildingPage(prev => prev + 1);
    else if (distance < -50 && buildingPage > 1) setBuildingPage(prev => prev - 1);
  }, [touchStartX, buildingPage, buildingTotalPages]);

  const handleBuildingTypeClick = useCallback((type: string) => {
    setBuildingType(type);  
    setBuildingPage(1); // 페이지를 1로 초기화
    // useEffect에서 자동으로 데이터를 fetch함
  }, []);

  // 관심공간 상태 변경 핸들러
  const handleFavoriteChange = useCallback((spaceId: string, isFavorite: boolean) => {
    // 서버 반영 즉시 최신 관심공간 목록을 다시 받아옴
    fetchFavorites();
  }, [fetchFavorites]);

  // Memoized values
  const isLoggedInValue = useMemo(() => isLoggedIn, [isLoggedIn]);
  const recentSpacesValue = useMemo(() => recentSpaces, [recentSpaces]);
  const buildingSpacesValue = useMemo(() => buildingSpaces, [buildingSpaces]);
  const loadingValue = useMemo(() => loading, [loading]);

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="animate-pulse">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: isMobile ? 4 : 8 }).map((_, index) => (
          <div key={index} className="bg-gray-200 rounded-lg h-64"></div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* 헤더 */}
      <header className="px-4 lg:px-6 py-4">
        {/* 모바일 헤더 */}
        <div className="flex flex-col items-center space-y-4 lg:hidden">
          <Suspense fallback={<div className="w-32 h-8 bg-gray-200 rounded animate-pulse"></div>}>
            <Logo />
          </Suspense>
          <div className="flex space-x-2">
            <select value={region} onChange={e => setRegion(e.target.value)} className="border rounded px-3 py-1 text-sm">
              <option value="">지역별</option>
              <option value="서울">서울</option>
              <option value="경기도">경기도</option>
            </select>
            <select value={sort} onChange={e => setSort(e.target.value)} className="border rounded px-3 py-1 text-sm">
              <option value="">정렬</option>
              <option value="rating">후기 순</option>
              <option value="distance">가까운 순</option>
              <option value="priceLow">가격 낮은 순</option>
            </select>
          </div>
          <div className="flex w-full justify-between items-center">
            <Suspense fallback={<div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>}>
              <SidebarMenu
                isLoggedIn={isLoggedInValue}
                unreadSpaceNoti={unreadSpaceNoti}
                unreadReservationNoti={unreadReservationNoti}
              />
            </Suspense>
            <div className="flex items-center gap-2">
              {isLoggedInValue && (
                <button
                  onClick={handleLogout}
                  className="bg-red-500 text-white px-4 py-2 rounded text-sm"
                >
                  로그아웃
                </button>
              )}
              <button aria-label="메뉴 열기" onClick={() => setIsSlideOverOpen(true)} className="p-2 rounded hover:bg-gray-100">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-7 h-7">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
  
        {/* 데스크탑 헤더 */}
        <div className="hidden lg:flex items-center justify-between relative w-full">
          {/* 왼쪽 */}
          <Suspense fallback={<div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>}>
            <SidebarMenu
              isLoggedIn={isLoggedInValue}
              unreadSpaceNoti={unreadSpaceNoti}
              unreadReservationNoti={unreadReservationNoti}
            />
          </Suspense>
  
          {/* 중앙 로고 (absolute로 고정) */}
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <Suspense fallback={<div className="w-32 h-8 bg-gray-200 rounded animate-pulse"></div>}>
              <Logo />
            </Suspense>
          </div>
  
          <div className="flex space-x-2 items-center">
            <select value={region} onChange={e => setRegion(e.target.value)} className="border rounded px-3 py-1 text-base">
              <option value="">지역별</option>
              <option value="서울">서울</option>
              <option value="경기도">경기도</option>
            </select>
            <select value={sort} onChange={e => setSort(e.target.value)} className="border rounded px-3 py-1 text-base">
              <option value="">정렬</option>
              <option value="rating">후기 순</option>
              <option value="distance">가까운 순</option>
              <option value="priceLow">가격 낮은 순</option>
            </select>
            <button aria-label="메뉴 열기" onClick={() => setIsSlideOverOpen(true)} className="p-2 rounded hover:bg-gray-100">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-7 h-7">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>
  
      {/* 팝업 */}
      <Suspense fallback={null}>
        <Helmet>
          <title>BIN-GO | 도심 스마트 공간 찾기</title>
          <meta name="description" content="BIN-GO는 도시 속 유휴 공간을 쉽고 안전하게 찾고, 일정 기간만 보관하는 스마트 공유 플랫폼입니다." />
          <meta property="og:title" content="BIN-GO | 도심 스마트 공간 찾기" />
          <meta property="og:description" content="BIN-GO는 도시 속 유휴 공간을 쉽고 안전하게 찾고, 일정 기간만 보관하는 스마트 공유 플랫폼입니다." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://web.bin-go.kr/" />
        </Helmet>
        <Popup />
      </Suspense>

      {/* 슬라이드 오버 */}
      <Suspense fallback={null}>
        <SlideOver isOpen={isSlideOverOpen} onClose={() => setIsSlideOverOpen(false)} />
      </Suspense>
  
      {/* 데스크탑 버튼영역 */}
      <div className="hidden lg:flex flex-row flex-wrap justify-start px-4 lg:px-6 py-4 gap-2">
        <button onClick={handleUploadSpace} className="bg-blue-500 text-white px-4 py-2 rounded text-sm lg:text-base">
          공간 업로드
        </button>
        {isLoggedInValue && (
          <button onClick={handleLogout} className="bg-red-500 text-white px-4 py-2 rounded text-sm lg:text-base">
            로그아웃
          </button>
        )}
      </div>
  
      {/* 배너 섹션 */}
      <div className="mx-auto w-full px-4 lg:px-6 py-6">
        <Suspense fallback={<div className="h-32 md:h-40 lg:h-56 bg-gray-200 rounded-lg animate-pulse"></div>}>
          <Banner 
            // items={bannerItems}
            autoSlideInterval={5000}
            showDots={true}
            showArrows={true}
          />
        </Suspense>
      </div>

      {/* 메인 컨텐츠 컨테이너 */}
      <div className="mx-auto w-full px-4 lg:px-6 py-6">
        <h2 className="text-xl lg:text-2xl font-bold mb-4">최근등록 공간</h2>
      
        {/* MainContent 컴포넌트 */}
        {loadingValue ? (
          <LoadingSkeleton />
        ) : (
          <Suspense fallback={<LoadingSkeleton />}>
            <MainContent 
              spaces={recentSpacesValue} 
              loading={loadingValue} 
              onTouchStart={handleTouchStart} 
              onTouchEnd={handleTouchEnd}
              onFavoriteChange={handleFavoriteChange}
              isLoggedIn={isLoggedInValue}
            />
          </Suspense>
        )}
  
        {/* 페이지네이션 */}
        <div className="flex justify-center items-center space-x-4 py-4">
          <button
            type="button"
            disabled={recentPage === 1}
            onClick={() => setRecentPage(prev => Math.max(prev - 1, 1))}
            className="text-xl lg:text-2xl text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors"
          >
            &lt;
          </button>
          <span className="text-base lg:text-lg text-gray-500 font-normal">{recentPage} / {recentTotalPages}</span>
          <button
            type="button"
            disabled={recentPage === recentTotalPages}
            onClick={() => setRecentPage(prev => Math.min(prev + 1, recentTotalPages))}
            className="text-xl lg:text-2xl text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors"
          >
            &gt;
          </button>
        </div>
      </div>
  
      <div className="mx-auto w-full px-4 lg:px-6 py-6">
      {/* 제목 */}
      <h2 className="text-xl lg:text-2xl font-bold mb-4">유형별 공간</h2>

      {/* 건물 유형 버튼 */}
      <Suspense fallback={<div className="h-20 bg-gray-200 rounded-lg animate-pulse"></div>}>
        <BuildingTypeButtons
          selectedType={buildingType}
          onTypeClick={handleBuildingTypeClick}
        />
      </Suspense>

      {/* 선택된 건물 유형에 맞는 공간을 표시 */}
      {buildingType && (
        <div className="mx-auto w-full lg:px-6 py-6">
          {loadingValue ? (
            <LoadingSkeleton />
          ) : (
            <Suspense fallback={<LoadingSkeleton />}>
              <MainContent 
                spaces={buildingSpacesValue} 
                loading={loadingValue} 
                onTouchStart={handleTouchStart} 
                onTouchEnd={handleTouchEnd}
                onFavoriteChange={handleFavoriteChange}
                isLoggedIn={isLoggedInValue}
              />
            </Suspense>
          )}
        </div>
      )}

      {/* 건물유형별 공간 페이지네이션 */}
      {buildingType && (
        <Suspense fallback={<div className="h-8 bg-gray-200 rounded animate-pulse"></div>}>
          <Pagination 
            currentPage={buildingPage} 
            totalPages={buildingTotalPages} 
            onPageChange={(page: number) => setBuildingPage(page)} 
          />
        </Suspense>
      )}
    </div>
  
      <Suspense fallback={<div className="h-32 bg-gray-200 animate-pulse"></div>}>
        <Footer />
      </Suspense>
  
      {/* 모바일 고정 업로드 버튼 */}
      <div className="fixed bottom-0 left-0 w-full p-4 bg-white shadow-md flex justify-center z-50 lg:hidden">
        <button onClick={handleUploadSpace} className="bg-blue-500 text-white px-6 py-3 rounded-lg text-sm">
          공간 업로드
        </button>
      </div>
    </div>
  );
  }

export default Spaces;
